import * as React from 'react';
import { memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { NavbarBackgroundType } from '../navigation-bar.types';
import {
  useAnalytics,
  useIsEmployerApp,
  useNavigationContext,
  useResponsiveBreakPoints,
  WorkerNavLinkType,
  cn,
} from '@restworld/utils-common';
import { ActiveItemType } from './mobile-overlay-nav';
import { useSpringRef, useChain } from '@react-spring/web';
import { BaseModal } from '../../../';
import HamburgerComponent from './hamburger-component';
import { Avatar } from 'flowbite-react';
import NavMenuContent, { NavWorkerInfo } from './nav-menu-content';
import { useIntercom } from 'react-use-intercom';

export type AvatarMenuType = {
  avatarName?: string;
  avatarImageURL?: string;
  onAvatarImageError?: (url?: string) => void;
  onClickHandler?: () => void;
  isActiveLabel?: boolean;
  showImageOnly?: boolean;
};
interface DesktopOverlayNavProps extends AvatarMenuType {
  isAuthenticated?: boolean;
  background?: NavbarBackgroundType;
  navOverlayToggler: React.Dispatch<React.SetStateAction<boolean>>;
  isNavOverlay: boolean;
  onStartLogin: () => void;
  onStartSignup: () => void;
  onLogout?: () => void;
  children?: ReactNode;
  isAmbassador?: boolean;
  primaryItems: WorkerNavLinkType[];
  secondaryItems: WorkerNavLinkType[];
  localeComponent?: ReactNode;
  testId?: string;
  workerInfo?: NavWorkerInfo;
  onShowUploadCvModal?: () => void;
}

export const DesktopOverlayNav = memo((props: DesktopOverlayNavProps) => {
  const {
    background = 'white',
    isAuthenticated,
    avatarImageURL,
    onAvatarImageError,
    isNavOverlay,
    navOverlayToggler,
    onStartLogin,
    onStartSignup,
    workerInfo,
    onShowUploadCvModal,
  } = props;
  const [activeItem, setActiveItem] = useState<ActiveItemType>();
  const { isDesktop } = useResponsiveBreakPoints();
  const { trackEvent } = useAnalytics();
  const { isEmployerApp } = useIsEmployerApp();
  const { router } = useNavigationContext();
  const { update } = useIntercom();

  const onSelectActiveItem = useCallback((value?: ActiveItemType) => setActiveItem(value), []);

  const onClickMenuToggler = useCallback(() => {
    navOverlayToggler((prev) => !prev);
    onSelectActiveItem();
  }, [navOverlayToggler, onSelectActiveItem]);

  const navRef = useSpringRef();
  const liRef = useSpringRef();
  const subRef = useSpringRef();

  // On showMenu, start with nav animation, then nav items
  useChain(isNavOverlay ? [navRef, liRef] : [liRef, navRef], [0, isNavOverlay ? 0.4 : 0.6]);

  // When sub-items are shown, close liRef and open subRef
  useChain(activeItem?.subItems ? [liRef, subRef] : [subRef, liRef], [0, activeItem?.subItems ? 0.4 : 0.6]);

  useEffect(() => {
    if (isEmployerApp || router.pathname.includes('/locale')) return;
    update({ hideDefaultLauncher: isNavOverlay });
  }, [isEmployerApp, isNavOverlay, router.pathname, update]);

  if (!isDesktop) return null;

  return (
    <>
      <div className="flex flex-row gap-1 items-center w-fit">
        <div
          className="flex flex-row items-center justify-start gap-2 w-fit"
          onClick={() => {
            onClickMenuToggler();
          }}
        >
          {isAuthenticated ? (
            <Avatar
              size="sm"
              alt="avatar-img"
              rounded
              color="purple"
              // Custom img render to ensure non-square source photos get
              // cropped (object-cover) instead of stretched into the circular
              // frame. Flowbite's default uses object-fill which distorts.
              img={
                avatarImageURL
                  ? (imgProps) => (
                      <img
                        {...imgProps}
                        src={avatarImageURL}
                        onError={() => onAvatarImageError?.(avatarImageURL)}
                        className={`${imgProps.className} object-cover`}
                      />
                    )
                  : undefined
              }
              className="hover:cursor-pointer min-w-[32px]"
            />
          ) : null}
          <HamburgerComponent background={background} isOpen={isNavOverlay} />
        </div>
      </div>
      <BaseModal
        open={isNavOverlay}
        onClose={() => onClickMenuToggler()}
        variant="right"
        noPadding={true}
        hideHeader
        footer={null}
        closeIcon={null}
        modalStyles={['rounded-tl-none bg-neutral-10 lg:pb-0 lg:w-[288px] lg:max-w-[288px] lg:min-w-0']}
      >
        <NavMenuContent
          onCloseMenu={onClickMenuToggler}
          avatarImageURL={avatarImageURL}
          workerInfo={workerInfo}
          onStartSignup={() => {
            onStartSignup();
            trackEvent('initiate_create_account', {
              origin: 'sidebar',
            });
          }}
          onStartLogin={() => {
            onStartLogin();
            trackEvent('initiate_signin', { login_state: false, user_type: null });
          }}
          onShowUploadCvModal={onShowUploadCvModal}
        />
        {/* <div className="flex flex-col gap-2 w-full " data-cy={testId}>
          <TopItem isHeader={isSubItemAvailable}>
            {isSubItemAvailable ? (
              <Button variant="ghost" onClick={() => onSelectActiveItem()}>
                <Title
                  textColor="black"
                  content={t('common:back')}
                  icon="ArrowLeftIcon"
                  fontWeight="semibold"
                  twStyle={'underline underline-offset-2 lg:text-sm'}
                />
              </Button>
            ) : null}
            {!isSubItemAvailable && (
              <Title variant="h2-medium" fontWeight="semibold">
                Ciao{isAuthenticated && avatarName ? ` ${avatarName}` : ''}!{' '}
                {!isAuthenticated && (
                  <span role="img" aria-labelledby="ciao">
                    👋🏻
                  </span>
                )}
              </Title>
            )}
          </TopItem>
          {activeItem?.id !== 'who-we-are' && gettingStartedButton}
          <div className="flex flex-col gap-4 w-full px-8">
            {activeItem?.label ? (
              <div className="p-3.5">
                <Title
                  fontWeight="semibold"
                  textColor="primary-700"
                  twStyle={'lg:text-lg text-lg lg:leading-6.75 leading-6.75'}
                  fontSize="large"
                  content={activeItem?.label}
                />
              </div>
            ) : null}
            {
              // If there are no sub-items, show primary items
              !activeItem?.subItems ? (
                <NavLabelItem
                  onSelectActiveItem={onSelectActiveItem}
                  frontNavItems={frontNavItems}
                  avatarName={avatarName}
                  activeItem={activeItem}
                  transitions={primaryTransitions}
                  isPrimary={true}
                />
              ) : null
            }
            {
              // If there are sub-items, show sub-items
              activeItem?.subItems ? (
                <NavLabelItem
                  onSelectActiveItem={onSelectActiveItem}
                  frontNavItems={frontNavItems}
                  avatarName={avatarName}
                  activeItem={activeItem}
                  transitions={subItemTransitions}
                />
              ) : null
            }
            {activeItem?.id === 'who-we-are' && gettingStartedButton}
            {isSubItemAvailable ? null : localeComponent ? <LocaleItem localeComponent={localeComponent} /> : null}
            {!isSubItemAvailable && !isAuthenticated && !isAmbassador ? (
              <div className="flex flex-col gap-2 w-full mt-6">
                <Button
                  data-cy="login-button-desktop"
                  fullwidth={true}
                  variant="normal"
                  title={t('common:login')}
                  onClick={() => {
                    onStartLogin();
                    trackEvent('initiate_signin', { login_state: false, user_type: null });
                  }}
                />
                <Button
                  fullwidth={true}
                  variant="secondary-outline"
                  title={t('common:create_account')}
                  onClick={() => {
                    onStartSignup();
                    // trackEvent('initiate_create_account', {
                    //   origin: 'sidebar',
                    // });
                  }}
                />
              </div>
            ) : null}
          </div>
        </div> */}
      </BaseModal>
    </>
  );
});

const TopItem = ({ isHeader, className, ...props }: any) => (
  <div
    className={cn(
      'flex flex-row items-center justify-start w-full border-b border-gray-300 py-4 px-8 pl-[46px]',
      isHeader ? 'pt-12' : '',
      className
    )}
    {...props}
  />
);
